import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, signal, TemplateRef, ViewChild, model, effect, computed, input } from "@angular/core";
import { FormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ButtonComponent } from '../../../../../../@core/components/ui/button/button.component';
import { CheckboxComponent } from '../../../../../../@core/components/ui/checkbox/checkbox.component';
import { IDocumentVersion, IFile, ILinkDocument, IUser } from "../../../../../../@core/models/model";
import { ToastService } from "../../../../../../@core/services/toast.service";
import { FileSelectionComponent } from "../../../../../../@shared/components/file-selection/file-selection.component";
import { FileDropTypes } from "../../../../../../@shared/components/file-drop/file-drop.types";
import { DateFormatterPipe } from "../../../../../../@shared/pipes/dateFormater.pipe";
import { UserAvatarPipe } from "../../../../../../@shared/pipes/user-avatar-pipe";
import { UserNameDisplayComponent } from "../../../../../../@shared/components/user-name-display/user-name-display.component";
import {
    FileSelectionConfig,
    FileSelectionChangeEvent,
    FileSelectionSubmitEvent,
    FileItem,
    TabType,
    FetchFunction
} from "../../../../../../@shared/components/file-selection/file-selection.model";
import type { DropdownOption } from "../../../../../../@shared/components/file-selection/file-selection.model";
import { DOCUMENT_MODEL, IContract, KNOWLEDGE_SCOPE } from "../../../../../models/contract-management.model";
import { ContractService } from "../../../../../services/contracts.service";
import { LinkDocumentService } from "../../../../../services/link-document.service";
import { KnowledgeBaseService } from "../../../../../services/knowledgebase.service";
import { ProjectService } from "../../../../../services/project.service";
import { RepositoryService } from "../../../../../services/repository.service";
import { VersionService } from "../../../../../services/version.service";
import { CompareService } from "../compare.service";
import { FILE_TYPE } from 'src/app/@core/models/enums';
import { FileIconComponent } from 'src/app/@shared/components/file-icon/file-icon.component';
import { TooltipModule } from 'primeng/tooltip';

const ALL_KNOWLEDGE_FOLDER_PREFIX = 'all-knowledge-';



@Component({
    selector: 'app-compare-selection',
    templateUrl: './compare-selection.component.html',
    styleUrl: './compare-selection.component.scss',
    providers: [],
    imports: [
        CommonModule,
        FormsModule,
        FileSelectionComponent,
        DateFormatterPipe,
        ButtonComponent,
        CheckboxComponent,
        UserNameDisplayComponent,
        FileIconComponent,
        TooltipModule
    ],
    standalone: true,
})
export class CompareSelectionComponent implements OnInit {

    public maxFilesCanBeSelected = 19
    public maxFileSize: number = 30 * 1000 * 1000
    public allowedFileTypes: string[] = [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC];

    userProfilePicture: { [key: string]: string } = {}
    private userAvatarPipe = new UserAvatarPipe();

    projects: { _id: string; name: string }[] = [];
    projectDropdownOptions = signal<DropdownOption[]>([]);
    workspaceKnowledgeBases: { _id: string; name: string; files?: any[] }[] = [];
    workspaceKnowledgeDropdownOptions = signal<DropdownOption[]>([]);
    activeDropdownOptions = signal<DropdownOption[]>([]);

    // File selection component state - two-way binding with parent
    visible = model<boolean>(true);
    fileSelectionVisible = signal<boolean>(true);
    fileSelectionConfig = computed<FileSelectionConfig>(() => ({
        title: 'Compare Document',
        maxTotalUploadedAttachments: 19,
        localUploadMaxFileSize: 50 * 1000 * 1000, // 50MB limit for individual locally uploaded files
        showTotalAttachmentsCount: true,
        allowedFileTypes: [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC],
        dialogWidth: '60vw',
        dialogHeight: '90vh',
        submitButtonLabel: 'Compare',
        cancelButtonLabel: 'Cancel',
        searchPlaceholder: 'Search Document',
        defaultPageSize: 10,
        rowsPerPageOptions: [10, 20, 50],
        supportSubFolders: true,
        tabs: [
            {
                id: 'contract',
                label: 'Documents',
                type: TabType.SELECT
            },
            {
                id: 'repository-workspace',
                label: 'Workspace knowledge',
                type: TabType.SELECT
            },
            {
                id: 'repository-project',
                label: 'Project knowledge',
                type: TabType.SELECT
            },
            {
                id: 'version',
                label: 'Versions',
                type: TabType.SELECT
            },
            {
                id: 'linked',
                label: 'Linked documents',
                type: TabType.SELECT
            },
            {
                id: 'upload',
                label: 'Upload files',
                type: TabType.UPLOAD
            }
        ]
    }));

    @ViewChild('fileItemTemplate') fileItemTemplate!: TemplateRef<any>;

    @Input() contractId!: string;
    @Input() documentType!: string;
    @Input() selectedDocuments?: { _id: string, name: string, docModel: string }[];
    @Input() comparisonId?: string;
    /** Optional: when set, "Project knowledge" tab is enabled and scope can be project. */
    projectId = input<string | undefined>(undefined);
    @Output() closed = new EventEmitter<string | undefined>();

    constructor(
        private contractService: ContractService,
        private versionService: VersionService,
        private repositoryService: RepositoryService,
        private linkDocumentService: LinkDocumentService,
        private knowledgeBaseService: KnowledgeBaseService,
        private projectService: ProjectService,
        private toast: ToastService,
        private compareService: CompareService
    ) {
        // Sync fileSelectionVisible with visible model (two-way binding)
        effect(() => {
            this.fileSelectionVisible.set(this.visible());
        });
    }

    ngOnInit(): void {
        this.projectService.getProjects(0, 1000).subscribe((res) => {
            if (res?.success && res.data?.projects) {
                this.projects = res.data.projects;
            }
        });
    }

    private getProjectDropdownOptions(): DropdownOption[] {
        return this.projects.map((p) => ({
            id: p._id,
            label: p.name,
            value: p,
        }));
    }

    private prepareWorkspaceDropdownOptions(): void {
        const options: DropdownOption[] = [
            {
                id: 'all',
                label: 'All files',
                groupKey: 'metadata.kbId',
                groupLabel: 'metadata.kbName'
            }
        ];
        this.workspaceKnowledgeBases.forEach((kb) => {
            options.push({
                id: kb._id,
                label: kb.name,
                value: kb,
                groupKey: 'metadata.kbId',
                groupLabel: 'metadata.kbName'
            });
        });
        this.workspaceKnowledgeDropdownOptions.set(options);
    }

    private fetchAllWorkspaceKnowledgeFiles(
        event: FileSelectionChangeEvent,
        excludeDocs: string[]
    ): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareWorkspaceDropdownOptions();
        return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.WORKSPACE }).pipe(
            map((res) => {
                const files = res?.data ?? [];
                const filteredFiles = files.filter((file: IFile) => {
                    if (file.type !== 'FILE') return false;
                    if (excludeDocs.includes(file._id)) return false;
                    if (event.searchQuery && !file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())) return false;
                    return true;
                });
                const allFiles: FileItem[] = [];
                filteredFiles.forEach((file: any) => {
                    if (file.knowledgeBases?.length) {
                        for (const kb of file.knowledgeBases) {
                            const kbDetails = this.workspaceKnowledgeBases.find((k) => k._id === kb._id);
                            allFiles.push({
                                id: file._id,
                                name: file.name || 'Unknown',
                                size: file.size,
                                metadata: {
                                    originalFile: file,
                                    createdBy: file.createdBy,
                                    createdAt: file.createdAt,
                                    docModel: DOCUMENT_MODEL.REPOSITORY,
                                    kbId: kbDetails?._id,
                                    kbName: kbDetails?.name
                                }
                            });
                        }
                    } else {
                        allFiles.push({
                            id: file._id,
                            name: file.name || 'Unknown',
                            size: file.size,
                            metadata: {
                                originalFile: file,
                                createdBy: file.createdBy,
                                createdAt: file.createdAt,
                                docModel: DOCUMENT_MODEL.REPOSITORY,
                                kbId: 'file-repository',
                                kbName: 'Files'
                            }
                        });
                    }
                });
                const start = event.page * event.pageSize;
                const end = start + event.pageSize;
                return { items: allFiles.slice(start, end), totalRecords: allFiles.length };
            }),
            catchError(() => of({ items: [], totalRecords: 0 }))
        );
    }

    private fetchWorkspaceKnowledgeFiles(
        kbId: string,
        event: FileSelectionChangeEvent,
        excludeDocs: string[]
    ): Observable<{ items: FileItem[]; totalRecords: number }> {
        this.prepareWorkspaceDropdownOptions();
        return this.knowledgeBaseService.getKnowledgeBaseStoreById(kbId).pipe(
            map((res: any) => {
                const store = res?.data ?? res;
                if (!store?.files) return { items: [], totalRecords: 0 };
                const kb = this.workspaceKnowledgeBases.find((k) => k._id === kbId);
                let list: FileItem[] = (store.files || [])
                    .filter((item: any) => item.file && !excludeDocs.includes(item.file._id))
                    .map((item: any) => {
                        const file = item.file;
                        if (file.createdBy && !this.userProfilePicture[(file.createdBy as IUser)._id]) {
                            this.userProfilePicture[(file.createdBy as IUser)._id] = this.userAvatarPipe.transform(file.createdBy);
                        }
                        const createdByUser = item.createdBy ?? file.createdBy;
                        return {
                            id: file._id,
                            name: file.name || 'Unknown',
                            size: file.size,
                            metadata: {
                                originalFile: file,
                                createdBy: createdByUser,
                                createdAt: item.uploadedAt ?? file.createdAt,
                                docModel: DOCUMENT_MODEL.REPOSITORY,
                                kbId: kb?._id,
                                kbName: kb?.name
                            }
                        };
                    });
                if (event.searchQuery) {
                    const q = event.searchQuery.toLowerCase();
                    list = list.filter((f: FileItem) => f.name?.toLowerCase().includes(q));
                }
                const start = event.page * event.pageSize;
                const end = start + event.pageSize;
                return { items: list.slice(start, end), totalRecords: list.length };
            }),
            catchError(() => of({ items: [], totalRecords: 0 }))
        );
    }

    // Fetch function for file-selection component
    fileSelectionFetch: FetchFunction = (event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> => {
        const excludeDocs = this.selectedDocuments?.map(doc => doc._id) ?? [];
        excludeDocs.push(this.contractId);

        if (event.tabId === 'repository-project') {
            if (this.projectId()) {
                // Project already known from context — no dropdown needed
                this.activeDropdownOptions.set([]);
            } else {
                this.projectDropdownOptions.set(this.getProjectDropdownOptions());
                this.activeDropdownOptions.set(this.projectDropdownOptions());
            }
        } else if (event.tabId === 'repository-workspace') {
            if (event.dropdownId === 'all' || event.dropdownId) {
                this.activeDropdownOptions.set(this.workspaceKnowledgeDropdownOptions());
            }
        } else {
            this.projectDropdownOptions.set([]);
            this.activeDropdownOptions.set([]);
        }

        if (event.tabId === 'contract') {
            return this.fetchContracts(event, excludeDocs);
        } else if (event.tabId === 'repository-workspace') {
            if (event.dropdownId === 'all') {
                return this.fetchAllWorkspaceKnowledgeFiles(event, excludeDocs);
            } else if (event.dropdownId) {
                return this.fetchWorkspaceKnowledgeFiles(event.dropdownId, event, excludeDocs);
            } else {
                return this.knowledgeBaseService.getKnowledgeBaseStore({ scope: KNOWLEDGE_SCOPE.WORKSPACE }).pipe(
                    map((res) => {
                        this.workspaceKnowledgeBases = res?.data?.stores ?? [];
                        this.prepareWorkspaceDropdownOptions();
                        this.activeDropdownOptions.set(this.workspaceKnowledgeDropdownOptions());
                        return { items: [], totalRecords: 0 };
                    })
                );
            }
        } else if (event.tabId === 'repository-project') {
            const effectiveProjectId = this.projectId() || event.dropdownId;
            if (!effectiveProjectId) {
                return of({ items: [], totalRecords: 0 });
            }
            return this.fetchProjectKnowledgeFiles(effectiveProjectId, event, excludeDocs);
        } else if (event.tabId === 'version') {
            return this.fetchVersions(event, excludeDocs);
        } else if (event.tabId === 'linked') {
            return this.fetchLinkedDocuments(event, excludeDocs);
        }

        return of({ items: [], totalRecords: 0 });
    };

    private fetchContracts(event: FileSelectionChangeEvent, excludeDocs: string[]): Observable<{ items: FileItem[]; totalRecords: number }> {
        return this.contractService.getDocumentsByType("", {
            search: event.searchQuery || '',
            skip: event.page * event.pageSize,
            limit: event.pageSize,
            excludeDocs,
            projectId: this.projectId()
        }).pipe(
            map((res) => {
                if (res && res.success) {
                    const contracts = res.data?.contracts ?? [];

                    // Populate user avatars
                    contracts.forEach((contract: IContract) => {
                        if (contract.createdBy && !this.userProfilePicture[(contract.createdBy as IUser)._id]) {
                            this.userProfilePicture[(contract.createdBy as IUser)._id] = this.userAvatarPipe.transform(contract.createdBy);
                        }
                    });

                    // Filter out already selected documents
                    const filteredContracts = contracts.filter(contract =>
                        !excludeDocs.includes(contract._id)
                    );

                    const items: FileItem[] = filteredContracts.map((contract: IContract) => ({
                        id: contract._id,
                        name: contract.name,
                        metadata: {
                            originalContract: contract,
                            createdBy: contract.createdBy,
                            createdAt: contract.createdAt,
                            docModel: DOCUMENT_MODEL.CONTRACT
                        }
                    }));

                    return { items, totalRecords: res.data.total };
                }
                return { items: [], totalRecords: 0 };
            })
        );
    }

    private fetchRepositoryFiles(event: FileSelectionChangeEvent, excludeDocs: string[]): Observable<{ items: FileItem[]; totalRecords: number }> {
        const scope = event.tabId === 'repository-project' ? KNOWLEDGE_SCOPE.PROJECT : KNOWLEDGE_SCOPE.WORKSPACE;
        return this.repositoryService.getAnyDocTypeFiles({
            search: event.searchQuery || '',
            skip: event.page * event.pageSize,
            limit: event.pageSize,
            scope: scope,
            ...(this.projectId() ? { project: this.projectId() } : {})
        }).pipe(
            map((res) => {
                if (res && res.success) {
                    const files = res.data?.files ?? [];

                    // Populate user avatars
                    files.forEach((file: IFile) => {
                        if (file.createdBy && !this.userProfilePicture[(file.createdBy as IUser)._id]) {
                            this.userProfilePicture[(file.createdBy as IUser)._id] = this.userAvatarPipe.transform(file.createdBy);
                        }
                    });

                    // Filter out already selected documents
                    const filteredFiles = files.filter(file =>
                        !excludeDocs.includes(file._id)
                    );

                    const items: FileItem[] = filteredFiles.map((file: IFile) => ({
                        id: file._id,
                        name: file.name,
                        size: file.size,
                        metadata: {
                            originalFile: file,
                            createdBy: file.createdBy,
                            createdAt: file.createdAt,
                            docModel: DOCUMENT_MODEL.REPOSITORY
                        }
                    }));

                    return { items, totalRecords: res.data.total };
                }
                return { items: [], totalRecords: 0 };
            })
        );
    }

    private fetchProjectKnowledgeFiles(
        projectId: string,
        event: FileSelectionChangeEvent,
        excludeDocs: string[]
    ): Observable<{ items: FileItem[]; totalRecords: number }> {
        const project = this.projects.find((p) => p._id === projectId);
        if (!project) return of({ items: [], totalRecords: 0 });
        const allKnowledgeFolderId = ALL_KNOWLEDGE_FOLDER_PREFIX + projectId;

        if (event.parentFolderId === allKnowledgeFolderId) {
            return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT, project: projectId }).pipe(
                map((res: any) => {
                    const raw = res?.data ?? [];
                    const filtered = raw.filter((file: any) =>
                        !event.searchQuery || file.name?.toLowerCase().includes(event.searchQuery.toLowerCase())
                    );
                    const allFiles: FileItem[] = filtered
                        .filter((f: any) => f.type === 'FILE')
                        .filter((f: any) => !excludeDocs.includes(f._id))
                        .map((file: any) => {
                            if (file.createdBy && !this.userProfilePicture[(file.createdBy as IUser)._id]) {
                                this.userProfilePicture[(file.createdBy as IUser)._id] = this.userAvatarPipe.transform(file.createdBy);
                            }
                            return {
                                id: file._id,
                                name: file.name || 'Unknown',
                                size: file.size,
                                metadata: {
                                    originalFile: file,
                                    createdBy: file.createdBy,
                                    createdAt: file.createdAt,
                                    docModel: DOCUMENT_MODEL.REPOSITORY,
                                    projectId: project._id,
                                    projectName: project.name,
                                },
                            };
                        });
                    const start = event.page * event.pageSize;
                    const end = start + event.pageSize;
                    return { items: allFiles.slice(start, end), totalRecords: allFiles.length };
                }),
                catchError(() => of({ items: [], totalRecords: 0 }))
            );
        }

        if (event.parentFolderId) {
            return this.knowledgeBaseService.getKnowledgeBaseStoreById(event.parentFolderId).pipe(
                map((res: any) => {
                    const store = res?.data ?? res;
                    if (!store?.files) return { items: [], totalRecords: 0 };
                    const projectName = project.name;
                    const kbName = store.name || 'Unknown';
                    let list: FileItem[] = store.files
                        .filter((item: any) => item.file && !excludeDocs.includes(item.file._id))
                        .map((item: any) => {
                            const file = item.file;
                            if (file.createdBy && !this.userProfilePicture[(file.createdBy as IUser)._id]) {
                                this.userProfilePicture[(file.createdBy as IUser)._id] = this.userAvatarPipe.transform(file.createdBy);
                            }
                            return {
                                id: file._id,
                                name: file.name || 'Unknown',
                                size: file.size,
                                metadata: {
                                    originalFile: file,
                                    createdBy: file.createdBy,
                                    createdAt: file.createdAt,
                                    docModel: DOCUMENT_MODEL.REPOSITORY,
                                    projectId: project._id,
                                    projectName,
                                    kbId: store._id,
                                    kbName,
                                },
                            };
                        });
                    if (event.searchQuery) {
                        const q = event.searchQuery.toLowerCase();
                        list = list.filter((f: FileItem) => f.name?.toLowerCase().includes(q));
                    }
                    const start = event.page * event.pageSize;
                    const end = start + event.pageSize;
                    return { items: list.slice(start, end), totalRecords: list.length };
                }),
                catchError(() => of({ items: [], totalRecords: 0 }))
            );
        }

        return this.knowledgeBaseService.getKnowledgeBaseStore({
            scope: KNOWLEDGE_SCOPE.PROJECT,
            project: projectId,
            skip: event.page === 0 ? 0 : event.page * event.pageSize - 1,
            limit: event.page === 0 ? event.pageSize - 1 : event.pageSize,
            search: event.searchQuery || '',
        }).pipe(
            map((res) => {
                const stores = res?.data?.stores ?? [];
                const totalStores = res?.data?.total ?? 0;
                const storeItems: FileItem[] = stores.map((store: any) => ({
                    id: store._id,
                    name: store.name || 'Unknown',
                    isFolder: true,
                    metadata: { kbId: store._id, kbName: store.name },
                }));
                const allKnowledgeFolder: FileItem = {
                    id: allKnowledgeFolderId,
                    name: 'All Knowledge',
                    isFolder: true,
                    metadata: {},
                };
                const items: FileItem[] = event.page === 0 ? [allKnowledgeFolder, ...storeItems] : storeItems;
                const totalRecords = totalStores + 1;
                return { items, totalRecords };
            }),
            catchError(() => of({ items: [], totalRecords: 0 }))
        );
    }

    private fetchVersions(event: FileSelectionChangeEvent, excludeDocs: string[]): Observable<{ items: FileItem[]; totalRecords: number }> {
        return this.versionService.getVersions(this.contractId, {
            search: event.searchQuery || '',
            skip: event.page * event.pageSize,
            limit: event.pageSize
        }).pipe(
            map((res) => {
                if (res && res.success) {
                    const versions = res.data?.versions ?? [];

                    // Populate user avatars
                    versions.forEach((version: IDocumentVersion) => {
                        if (version.createdBy && !this.userProfilePicture[(version.createdBy as IUser)._id]) {
                            this.userProfilePicture[(version.createdBy as IUser)._id] = this.userAvatarPipe.transform(version.createdBy);
                        }
                    });

                    // Filter out already selected documents
                    const filteredVersions = versions.filter(version =>
                        !excludeDocs.includes(version._id)
                    );

                    const items: FileItem[] = filteredVersions.map((version: IDocumentVersion) => ({
                        id: version._id,
                        name: version.name,
                        metadata: {
                            originalVersion: version,
                            createdBy: version.createdBy,
                            createdAt: version.updatedAt,
                            docModel: DOCUMENT_MODEL.VERSION
                        }
                    }));

                    return { items, totalRecords: res.data.total };
                }
                return { items: [], totalRecords: 0 };
            })
        );
    }

    private fetchLinkedDocuments(event: FileSelectionChangeEvent, excludeDocs: string[]): Observable<{ items: FileItem[]; totalRecords: number }> {
        return this.linkDocumentService.fetchLinkDocument(this.contractId, {
            skip: event.page * event.pageSize,
            limit: event.pageSize
        }).pipe(
            map((res) => {
                if (res && res.success) {
                    const linkDocs = res.data?.data ?? [];

                    // Populate user avatars
                    linkDocs.forEach((linkDoc: ILinkDocument) => {
                        if (linkDoc.linkedBy && !this.userProfilePicture[(linkDoc.linkedBy as IUser)._id]) {
                            this.userProfilePicture[(linkDoc.linkedBy as IUser)._id] = this.userAvatarPipe.transform(linkDoc.linkedBy);
                        }
                    });

                    // Filter out already selected documents
                    const filteredLinkDocs = linkDocs.filter(linkDoc =>
                        !excludeDocs.includes(linkDoc.targetDocument._id)
                    );

                    const items: FileItem[] = filteredLinkDocs.map((linkDoc: ILinkDocument) => ({
                        id: linkDoc.targetDocument._id,
                        name: linkDoc.targetDocument.name,
                        metadata: {
                            originalLinkDoc: linkDoc,
                            linkedBy: linkDoc.linkedBy,
                            createdAt: linkDoc.createdAt,
                            targetDocument: linkDoc.targetDocument,
                            docModel: DOCUMENT_MODEL.LINKED_DOCUMENT
                        }
                    }));

                    return { items, totalRecords: res.data.total };
                }
                return { items: [], totalRecords: 0 };
            })
        );
    }

    async onFileSelectionSubmit(event: FileSelectionSubmitEvent) {
        if (!event.selectedFiles && !event.uploadedFiles) {
            this.toast.warn('Please select at least one document for comparison');
            return;
        }

        // Validate max selection limit
        const totalSelected = (event.allSelectedFiles?.length || 0) + (event.uploadedFiles?.length || 0);
        if (totalSelected > this.maxFilesCanBeSelected) {
            this.toast.warn(`You can only select a maximum of ${this.maxFilesCanBeSelected} documents for comparison`);
            return;
        }

        // Transform selected files to comparison format
        let selectedDocs: { docModel: string, contract: { _id: string, name: string } }[] = [];

        // Add pre-selected documents from input
        if (this.selectedDocuments) {
            selectedDocs = this.selectedDocuments.map(doc => ({
                docModel: doc.docModel,
                contract: { _id: doc._id, name: doc.name }
            }));
        }

        // Add newly selected files
        if (event.selectedFiles) {
            event.selectedFiles.forEach(group => {
                group.files.forEach(file => {
                    const docModel = file.metadata?.docModel || DOCUMENT_MODEL.CONTRACT;
                    selectedDocs.push({
                        docModel: docModel,
                        contract: {
                            _id: file.id,
                            name: file.name
                        }
                    });
                });
            });
        }

        // Handle uploaded files - add them to selectedDocs with UPLOADED_DOCUMENT docModel
        const uploadedDocs: File[] = event.uploadedFiles || [];
        uploadedDocs.forEach(file => {
            selectedDocs.push({
                docModel: DOCUMENT_MODEL.UPLOADED_DOCUMENT,
                contract: {
                    _id: file.name,
                    name: file.name
                }
            });
        });

        // Filter uploaded files that are in selectedDocs
        let uploadedAndSelectedDocs: any[] = [];
        if (selectedDocs.find((curr: any) => curr.docModel == DOCUMENT_MODEL.UPLOADED_DOCUMENT)) {
            const selectedUploadedDocs = selectedDocs.filter((curr: any) => curr.docModel == DOCUMENT_MODEL.UPLOADED_DOCUMENT).map((curr: any) => curr.contract.name)
            uploadedAndSelectedDocs = uploadedDocs.filter((file) => selectedUploadedDocs.includes(file.name))
        }

        try {
            const comparisonId = await this.createComparison(selectedDocs, uploadedAndSelectedDocs);
            this.closed.emit(comparisonId as string | undefined);
            setTimeout(() => {
                this.visible.set(false);
            }, 0);
        } catch (error) {
            this.toast.error('Failed to create comparison');
            console.error('Comparison creation error:', error);
            // Still close the dialog even on error, but don't emit comparisonId
            this.closed.emit(undefined);
            setTimeout(() => {
                this.visible.set(false);
            }, 0);
        }
    }

    onFileSelectionCancel() {
        this.visible.set(false);
        this.closed.emit(undefined);
    }

    createComparison(selectedDocs: any[], uploadedAndSelectedDocs: File[] = []) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            if (this.comparisonId) {
                formData.append('comparisonId', this.comparisonId);
            }
            formData.append('document', this.contractId);
            formData.append('compareDocuments', JSON.stringify(selectedDocs));
            if (uploadedAndSelectedDocs.length > 0) {
                uploadedAndSelectedDocs.forEach(file => {
                    formData.append('uploadedDocs', file);
                })
            }
            this.compareService.createComparisonWithUploadedDocs(formData).subscribe((res) => {
                if (res && res.success) {
                    resolve(res.data)
                } else {
                    reject([])
                }
            })
        })
    }

    getFileType(fileName: string): FILE_TYPE | undefined {
        const ext = fileName.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf':
                return FILE_TYPE.PDF;
            case 'doc':
            case 'docx':
                return FILE_TYPE.WORD;
            case 'xls':
            case 'xlsx':
                return FILE_TYPE.EXCEL;
            case 'ppt':
            case 'pptx':
                return FILE_TYPE.POWERPOINT;
            case 'html':
                return FILE_TYPE.HTML;
            case 'txt':
                return FILE_TYPE.TEXT;
            case 'csv':
                return FILE_TYPE.CSV;
            case 'md':
                return FILE_TYPE.MARKDOWN;
            case 'python':
                return FILE_TYPE.PYTHON;
            case 'json':
                return FILE_TYPE.JSON;
            case 'jpg':
                return FILE_TYPE.JPG;
            case 'png':
                return FILE_TYPE.PNG;
            case 'jpeg':
                return FILE_TYPE.JPEG;
            default:
                return undefined;
        }
    }


}
